import moment from 'moment';
import { mapState } from 'vuex';
import { get, isEmpty } from 'lodash';
import ApiService from '@/services/apiService';
const defaultTableOptions = {
  sortBy: ['result.success'],
  sortDesc: [false],
  page: 1,
  itemsPerPage: 10
};
const MAX_DELAY = 16000;
export default {
  name: 'CoreBackgroundJobsInfoDialog',
  props: {
    itemInfo: {
      type: Object,
      default: () => ({}),
      required: true
    },
    isOpen: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      tableOptions: {
        ...defaultTableOptions
      },
      expanded: [],
      serverItemsLength: null,
      items: [],
      singleExpand: true,
      parentTitle: '',
      fetchRunnerTimeout: null,
      isPaused: false,
      progressBarPercent: 0,
      errorCount: 0,
      delay: 4000,
      isProcessing: false,
      isFetching: false
    };
  },
  computed: {
    ...mapState('app', ['color']),
    userDisplayName() {
      const {
        firstName = '',
        lastName = ''
      } = this.itemInfo.userCreated || {};
      return `${firstName} ${lastName}`.trim();
    },
    userCreated() {
      return get(this.itemInfo, 'userCreated');
    },
    isTasksTotal() {
      return get(this.itemInfo, 'tasks');
    },
    numberOfDevices() {
      if (this.itemInfo.type === 'deviceStatesCsvExport') {
        return this.itemInfo.executions.matchingIds;
      }
      if (get(this.itemInfo, 'executions.params.deviceTemplate')) {
        return this.itemInfo.tasks.total - 1;
      }
      if (this.itemInfo.type === 'virtualDevicesEdit' && get(this.itemInfo, 'executions.deletionOfDevices')) {
        return this.itemInfo.tasks.total - 1;
      } else {
        return this.itemInfo.tasks.total;
      }
    },
    isExportForDigitalTwins() {
      return get(this.itemInfo, 'executions.params.type') === 'digitalTwin' || this.itemInfo.type === 'digitalTwinBulkDuplicate';
    },
    hasErrors() {
      return get(this.itemInfo, 'tasks.errors');
    },
    isTitle() {
      return get(this.itemInfo, 'title');
    },
    isState() {
      return get(this.itemInfo, 'state');
    },
    isCreatedAt() {
      return get(this.itemInfo, 'createdAt');
    },
    hasExecutions() {
      return !isEmpty(get(this.itemInfo, 'executions'));
    },
    tableLocalizations() {
      return {
        headers: [{
          text: this.$i18n_t('BackgroundJobs.BackgroundJobs.table.entityId'),
          value: 'entityId',
          sortable: true
        }, {
          text: this.$i18n_t('Common.status'),
          value: 'result.success',
          sortable: true
        }, {
          text: '',
          value: 'actions',
          sortable: false
        }],
        tableFooterProps: {
          itemsPerPageText: this.$i18n_t('Common.rowsPerPage'),
          itemsPerPageOptions: [10, 20, 50]
        }
      };
    },
    operationalStatus() {
      return [{
        text: this.$i18n_t('DigitalTwin.VirtualDevice.bulkUpdate.operationalStatus.unset'),
        value: 0
      }, {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.bulkUpdate.operationalStatus.inTransit'),
        value: 1
      }, {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.bulkUpdate.operationalStatus.readyForOperation'),
        value: 2
      }, {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.bulkUpdate.operationalStatus.inOperation'),
        value: 3
      }, {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.bulkUpdate.operationalStatus.temporaryInactive'),
        value: 4
      }, {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.bulkUpdate.operationalStatus.decommissioned'),
        value: 5
      }];
    }
  },
  watch: {
    tableOptions: {
      handler() {
        this.fetchTasks();
      },
      deep: true
    },
    itemInfo() {
      this.calculateAndSetProgressBar();
      if (this.itemInfo.state !== 'in progress') {
        clearTimeout(this.fetchRunnerTimeout);
        this.fetchTasks();
      }
      const parentId = this.itemInfo.executions ? this.itemInfo.executions.parentId : null;
      if (!parentId) {
        return;
      }
      ApiService.digitalTwinGet(this, parentId).then(twin => {
        this.parentTitle = twin.title;
      }).catch(error => {
        console.error(error);
      });
    },
    isOpen() {
      if (!this.isOpen || this.itemInfo.state !== 'in progress') {
        clearTimeout(this.fetchRunnerTimeout);
        return;
      }
      this.fetchRunner();
    }
  },
  mounted() {
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  },
  methods: {
    get,
    localizeDate(a, options = {}) {
      if (a) {
        moment.locale(this.$store.state.app.language || window.navigator.userLanguage || window.navigator.language);
        const format = options.format || 'L LT';
        return moment(a).format(format);
      }
      return '-';
    },
    getItemPerPage(newPageSize) {
      this.tableOptions.itemsPerPage = newPageSize;
    },
    updatePagination(e) {
      this.tableOptions.page = e.page;
    },
    closeJobDetails() {
      this.items = [];
      this.progressBarPercent = 0;
      this.isProcessing = false;
      this.$emit('close');
    },
    setCurrentTablePage(value) {
      this.tableOptions.page = value;
    },
    handleExpansion(item, state) {
      const itemIndex = this.expanded.indexOf(item);
      state ? this.expanded.splice(itemIndex, 1) : this.expanded.push(item);
    },
    fetchTasks() {
      this.isFetching = true;
      ApiService.fetchBackgroundRunnerTasksLogs(this.itemInfo.id, (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage, this.tableOptions.itemsPerPage, this.tableOptions.sortBy[0], this.tableOptions.sortDesc[0]).then(res => {
        res.tasks.forEach(task => {
          // if task was cancelled, still set the success-attribute, so the sorting works
          // (otherwise error and cancelled states are mixed)
          if (!task.result) {
            task.result = {
              success: -1
            };
          }
        });
        this.items = res.tasks;
        this.serverItemsLength = res.totalCount;
        this.isProcessing = this.itemInfo.state !== 'canceled' && this.progressBarPercent < 100;
      }).catch(() => {
        // do nothing
      }).finally(() => {
        this.isFetching = false;
      });
    },
    fetchRunner(forceRun = false) {
      const performRun = () => {
        ApiService.fetchBackgroundRunner(this.itemInfo.id).then(async runner => {
          this.itemInfo = runner;
          this.calculateAndSetProgressBar();
          this.fetchRunner();
        }).catch(() => {
          if (this.errorCount > 5) {
            return;
          }
          this.errorCount++;
          this.delay = Math.min(this.delay * 2, MAX_DELAY);
        });
      };
      if (forceRun) {
        performRun();
        return;
      }
      if (this.isPaused || !this.isOpen) {
        return;
      }
      this.fetchRunnerTimeout = setTimeout(performRun, this.delay);
    },
    triggerTaskRetry(task) {
      ApiService.retryBackgroundRunnerTask(this.itemInfo.id, task.id).catch(e => {
        this.$snack(e.response.data.message, 'error');
      }).finally(() => {
        this.progressBarPercent = 0;
        this.fetchRunner(true);
        this.fetchTasks();
      });
    },
    handleVisibilityChange() {
      const visibilityState = document.visibilityState;
      if (visibilityState === 'hidden') {
        this.pauseFetchRunner();
      } else if (visibilityState === 'visible') {
        this.resumeFetchRunner();
      }
    },
    pauseFetchRunner() {
      this.isPaused = true;
    },
    resumeFetchRunner() {
      this.isPaused = false;
      this.fetchRunner();
    },
    calculateAndSetProgressBar() {
      const finished = this.itemInfo.tasks.done + this.itemInfo.tasks.errors;
      const total = this.itemInfo.tasks.total;
      this.progressBarPercent = finished / total * 100;
    },
    getStatus(item) {
      if (!item.result || item.result.success === -1) {
        if (this.itemInfo.type === 'deviceStatesCsvExport' && item.state === 'created') {
          return this.$i18n_t('BackgroundJobs.BackgroundJobs.createdAndWaiting');
        }
        return this.$i18n_t('BackgroundJobs.BackgroundJobs.canceled');
      }
      if (item.result && item.result.success) {
        switch (this.itemInfo.type) {
          case 'virtualDevicesCsvImport':
          case 'digitalTwinBulkDuplicate':
            return this.$i18n_t('BackgroundJobs.BackgroundJobs.successfullyImported');
          case 'virtualDevicesEdit':
            if (get(item, 'actions.params.taskCreationType')) {
              return this.$i18n_t('BackgroundJobs.BackgroundJobs.taskCreation');
            }
            if (this.itemInfo.executions.deletionOfDevices) {
              return this.$i18n_t('BackgroundJobs.BackgroundJobs.sucesfullyDeleted');
            }
            return this.$i18n_t('BackgroundJobs.BackgroundJobs.successfullyEdited');
          case 'deviceTemplateSync':
            {
              const actionsParams = get(item, 'actions.params');
              if (actionsParams && (actionsParams.taskCreationType || actionsParams.deviceTemplate)) {
                return this.$i18n_t('BackgroundJobs.BackgroundJobs.taskCreation');
              }
              return this.$i18n_t('BackgroundJobs.BackgroundJobs.successfullyEdited');
            }
          case 'deviceStatesCsvExport':
            {
              if (item.entityId === this.itemInfo.id) {
                // postprocessing task entityId is same as backgroundRunner id
                return this.$i18n_t('BackgroundJobs.BackgroundJobs.successfullyCombinedAndZipped');
              }
              const actionsParams = get(item, 'actions.params.taskCreationType');
              if (actionsParams === 'createBulkExportTasks') {
                return this.$i18n_t('BackgroundJobs.BackgroundJobs.taskCreation');
              }
              return this.$i18n_t('BackgroundJobs.BackgroundJobs.successfullyExported');
            }
          default:
            return `not supported task type ${this.itemInfo.type}`;
        }
      } else {
        return this.$i18n_t('BackgroundJobs.BackgroundJobs.error');
      }
    },
    constructUrl(item) {
      if (get(item, 'result.data.properties.type') === 'digitalTwin' && get(item, 'result.success')) {
        return `/digitaltwin-mgmt/${get(item, 'result.data.id')}`;
      }
      if (get(item, 'result.data.properties.type') === 'virtualDevice' && get(item, 'result.success')) {
        return `/virtual-devices/${get(item, 'result.data.id')}`;
      }
      if (item.type === 'deviceStatesCsvExport') {
        if (get(item, 'actions.params.type') === 'virtualDevice') {
          return `/virtual-devices/${item.entityId}`;
        } else {
          return `/digitaltwin-mgmt/${item.entityId}`;
        }
      }
      return null;
    },
    entityUrlPrefix(item) {
      if (get(item, 'result.data.properties.type') === 'digitalTwin' && get(item, 'result.success')) {
        return `${this.$i18n_t('Common.twinId')}: `;
      }
      if (get(item, 'result.data.properties.type') === 'virtualDevice' && get(item, 'result.success')) {
        return `${this.$i18n_t('Common.virtualId')}: `;
      }
      return '#';
    }
  }
};